import {
    collection,
    deleteDoc,
    doc,
    getDoc,
    getDocs,
    orderBy,
    query,
    runTransaction,
    serverTimestamp,
    Timestamp,
    updateDoc,
    where,
} from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '@/config/firebase';
import { app } from '@/config/firebase';
import { SELL_DAYS } from '../utils/vacationRules';
import { resolveDpOutcome } from '../utils/resolveDpOutcome';
import type {
    ClientApprovalStatus,
    ClientInvite,
    SellRequestStatus,
    VacationRequest,
    VacationRequestCreate,
    VacationRequestStatus,
    VacationSegment,
} from '../models/vacation.types';

const CREATE_UNAVAILABLE_MESSAGE =
    'Não foi possível enviar a solicitação agora. Tente novamente; se o problema persistir, procure o DP.';

const CREATE_TECHNICAL_CODES = [
    'functions/internal',
    'functions/unavailable',
    'functions/deadline-exceeded',
    'functions/not-found',
    'functions/unknown',
];

function toCreateError(error: unknown): Error {
    const code = (error as { code?: string } | null)?.code ?? '';
    if (CREATE_TECHNICAL_CODES.includes(code)) return new Error(CREATE_UNAVAILABLE_MESSAGE);
    return error instanceof Error ? error : new Error(CREATE_UNAVAILABLE_MESSAGE);
}

class VacationRequestServiceClass {
    private collectionName = 'vacationRequests';

    async create(data: VacationRequestCreate): Promise<string> {
        const createVacationRequest = httpsCallable<
            {
                modality: string;
                firstStartDate: string;
                firstEndDate: string;
                secondStartDate: string;
                sellDaysRequested: boolean;
                managerEmail: string;
                clientEmails: string[];
            },
            { id: string }
        >(getFunctions(app, 'us-central1'), 'createVacationRequest');

        const [first, second] = data.segments;

        try {
            const result = await createVacationRequest({
                modality: data.modality,
                firstStartDate: first.startDate,
                firstEndDate: second ? first.endDate : '',
                secondStartDate: second?.startDate ?? '',
                sellDaysRequested: data.sellDays > 0,
                managerEmail: data.managerEmail,
                clientEmails: data.clientEmails,
            });
            return result.data.id;
        } catch (error) {
            throw toCreateError(error);
        }
    }

    async getByCollaboratorEmail(email: string): Promise<VacationRequest[]> {
        if (!email) return [];

        const snapshot = await getDocs(
            query(
                collection(db, this.collectionName),
                where('collaboratorEmail', '==', email),
                orderBy('requestedAt', 'desc')
            )
        );

        return snapshot.docs.map((snap) => this.formatRequest(snap.id, snap.data()));
    }

    async getById(id: string): Promise<VacationRequest | null> {
        const snap = await getDoc(doc(db, this.collectionName, id));
        if (!snap.exists()) return null;
        return this.formatRequest(snap.id, snap.data());
    }

    async getForManager(managerEmail: string): Promise<VacationRequest[]> {
        if (!managerEmail) return [];

        const snapshot = await getDocs(
            query(
                collection(db, this.collectionName),
                where('managerEmail', '==', managerEmail),
                orderBy('requestedAt', 'desc')
            )
        );

        return this.excludeDrafts(snapshot.docs.map((snap) => this.formatRequest(snap.id, snap.data())));
    }

    async listAll(): Promise<VacationRequest[]> {
        const snapshot = await getDocs(
            query(collection(db, this.collectionName), orderBy('requestedAt', 'desc'))
        );

        return this.excludeDrafts(snapshot.docs.map((snap) => this.formatRequest(snap.id, snap.data())));
    }

    /**
     * Solicitação em carência existe só para o dono — `getByCollaboratorEmail` é a única leitura que
     * a devolve. Filtrar em memória, e não com `where`: desigualdade em `status` exigiria
     * `orderBy('status')` como primeira ordenação, derrubando o `orderBy('requestedAt', 'desc')`.
     */
    private excludeDrafts(requests: VacationRequest[]): VacationRequest[] {
        return requests.filter((request) => request.status !== 'Draft');
    }

    async decide(input: {
        requestId: string;
        kind: 'approve' | 'reject';
        note: string;
        managerEmail: string;
        sellStatus: SellRequestStatus;
        correctionRevision?: number;
    }): Promise<void> {
        const docRef = doc(db, this.collectionName, input.requestId);

        const isCorrection = (input.correctionRevision ?? 0) > 0;
        const isRejection = input.kind === 'reject';

        await runTransaction(db, async (transaction) => {
            const snapshot = await transaction.get(docRef);
            const current = snapshot.data();
            if (!current) throw new Error('Vacation request not found');

            const storedClientStatus = (current.clientApproval?.status ?? 'None') as ClientApprovalStatus;

            let status: string;
            if (isCorrection && isRejection) {
                status = 'PendingCorrection';
            } else {
                const clientAlreadyDone = storedClientStatus !== 'Pending';
                const approvedNextStatus = clientAlreadyDone ? 'PendingDP' : 'PendingClient';
                status = input.kind === 'approve' ? approvedNextStatus : 'Rejected';
            }

            transaction.update(docRef, this.buildManagerDecisionPayload(input, status, isCorrection, isRejection));
        });
    }

    private buildManagerDecisionPayload(
        input: {
            kind: 'approve' | 'reject';
            note: string;
            managerEmail: string;
            sellStatus: SellRequestStatus;
        },
        status: string,
        isCorrection: boolean,
        isRejection: boolean
    ): Record<string, any> {
        const updatePayload: Record<string, any> = {
            status,
            managerApproval: {
                status: input.kind === 'approve' ? 'Approved' : 'Rejected',
                by: input.managerEmail,
                at: serverTimestamp(),
                note: input.note || null,
            },
            updatedAt: serverTimestamp(),
        };

        if (isCorrection && isRejection) {
            updatePayload.lastCorrectionRejection = {
                stage: 'manager',
                by: input.managerEmail,
                note: input.note || null,
                at: serverTimestamp(),
            };
        }

        if (input.sellStatus === 'Pending' && input.kind === 'reject') {
            updatePayload['sellRequest.status'] = 'Rejected';
            updatePayload['sellRequest.note'] = null;
            updatePayload['sellRequest.decidedBy'] = input.managerEmail;
            updatePayload['sellRequest.decidedAt'] = serverTimestamp();
        }

        return updatePayload;
    }

    async decideAsDp(input: {
        requestId: string;
        dpEmail: string;
        sellDecision?: 'approve' | 'reject';
        segmentDecisions: Array<{ order: number; kind: 'approve' | 'reject'; note: string }>;
    }): Promise<void> {
        const docRef = doc(db, this.collectionName, input.requestId);
        const docData = await getDoc(docRef);
        const request = docData.data();
        if (!request) throw new Error('Vacation request not found');

        const segments: VacationSegment[] = request.segments ?? [];
        const dpSegmentDecisions: Record<string, any> = {
            ...request.dpSegmentDecisions,
        };

        const rejectedReasons: string[] = [];

        input.segmentDecisions.forEach((seg) => {
            const previousDecision = request.dpSegmentDecisions?.[String(seg.order)];
            if (previousDecision?.status === 'Approved') {
                return;
            }

            const segmentData = segments.find((s: VacationSegment) => s.order === seg.order);

            dpSegmentDecisions[String(seg.order)] = {
                status: seg.kind === 'approve' ? 'Approved' : 'Rejected',
                note: seg.kind === 'reject' ? seg.note : null,
                by: input.dpEmail,
                at: serverTimestamp(),
                startDate: segmentData?.startDate,
                endDate: segmentData?.endDate,
            };

            if (seg.kind === 'reject' && seg.note) {
                rejectedReasons.push(seg.note);
            }
        });

        const nextStatus = resolveDpOutcome({
            segmentDecisions: Object.fromEntries(
                input.segmentDecisions.map((seg) => [
                    String(seg.order),
                    { decision: seg.kind, note: seg.note },
                ])
            ),
            previousDecisions: request.dpSegmentDecisions,
            totalSegments: segments.length,
        });

        const currentSell = request.sellRequest ?? null;
        const hasPendingSell = currentSell && currentSell.requested && currentSell.status === 'Pending';
        const decidesSell = nextStatus === 'Scheduled' && Boolean(input.sellDecision);

        const allRejected = nextStatus === 'Rejected';
        const allApproved = nextStatus === 'Scheduled';

        await updateDoc(docRef, {
            status: nextStatus,
            dpSegmentDecisions,
            dpApproval:
                allApproved || allRejected
                    ? {
                          status: allApproved ? 'Approved' : 'Rejected',
                          by: input.dpEmail,
                          at: serverTimestamp(),
                          note: rejectedReasons.length > 0 ? rejectedReasons.join('; ') : null,
                      }
                    : request.dpApproval,
            updatedAt: serverTimestamp(),
            ...(decidesSell && currentSell
                ? {
                      sellRequest: {
                          ...currentSell,
                          status: input.sellDecision === 'approve' ? 'Approved' : 'Rejected',
                          decidedBy: input.dpEmail,
                          decidedAt: serverTimestamp(),
                      },
                  }
                : hasPendingSell && allRejected
                ? {
                      sellRequest: {
                          ...currentSell,
                          status: 'Rejected',
                          decidedBy: input.dpEmail,
                          decidedAt: serverTimestamp(),
                      },
                  }
                : {}),
        });
    }

    async submitCorrection(input: {
        requestId: string;
        corrections: Array<{ order: number; startDate: string; endDate: string }>;
    }): Promise<void> {
        const docRef = doc(db, this.collectionName, input.requestId);
        const docData = await getDoc(docRef);
        const request = docData.data() as VacationRequest | undefined;
        if (!request) throw new Error('Vacation request not found');

        const correctionRevision = (request.correctionRevision ?? 0) + 1;
        const correctionsByOrder = new Map(input.corrections.map((correction) => [correction.order, correction]));

        const updatedSegments = request.segments
            .map((seg) => {
                const correction = correctionsByOrder.get(seg.order);
                if (correction) {
                    return {
                        ...seg,
                        startDate: correction.startDate,
                        endDate: correction.endDate,
                    };
                }
                return seg;
            })
            .sort((a, b) => a.startDate.localeCompare(b.startDate));

        const clientEmails = request.clientEmails ?? [];
        const resetClientInvites = clientEmails.map((email) => ({
            email,
            resendCount: 0,
        }));

        await updateDoc(docRef, {
            segments: updatedSegments,
            status: 'PendingManager',
            correctionRevision,
            managerApproval: { status: 'Pending' },
            clientApproval: { status: clientEmails.length > 0 ? 'Pending' : 'None' },
            dpApproval: { status: 'Pending' },
            clientInvites: resetClientInvites,
            statusChangedAt: serverTimestamp(),
            statusChangedBy: null,
            updatedAt: serverTimestamp(),
        });
    }

    /**
     * Exclusão definitiva durante a carência. Não escreve nada antes de apagar: as rules só liberam
     * `delete` sobre um documento ainda em `Draft`, e qualquer `updateDoc` prévio seria negado.
     */
    async deleteDraft(input: { requestId: string }): Promise<void> {
        await deleteDoc(doc(db, this.collectionName, input.requestId));
    }

    /** Lê `clientEmails`; documentos antigos gravaram um único `clientEmail` string. */
    private readClientEmails(data: Record<string, unknown>): string[] | null {
        if (Array.isArray(data.clientEmails)) {
            return data.clientEmails as string[];
        }
        if (typeof data.clientEmail === 'string' && data.clientEmail) {
            return [data.clientEmail];
        }
        return null;
    }

    async resendClientEmail(input: { requestId: string; email: string }): Promise<void> {
        const functions = getFunctions(app, 'us-central1');
        const resend = httpsCallable<
            { requestId: string; email: string },
            { resentAt: string }
        >(functions, 'resendVacationClientEmail');

        await resend(input);
    }

    /**
     * Escrita direta, autorizada por `isOwnerConfirmation()` nas rules. Os cinco campos e os
     * carimbos de `serverTimestamp()` são exigidos pela regra — mudar este payload sem mexer nas
     * rules faz a escrita ser recusada.
     */
    async confirmDraft(input: { requestId: string }): Promise<void> {
        const email = getAuth().currentUser?.email;
        if (!email) {
            throw new Error(
                'vacations/sessao-invalida: Não foi possível identificar seu usuário. Recarregue a página e tente de novo.'
            );
        }

        await updateDoc(doc(db, this.collectionName, input.requestId), {
            status: 'PendingManager',
            statusChangedAt: serverTimestamp(),
            statusChangedBy: email,
            openedAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
        });
    }

    private formatRequest(id: string, data: Record<string, unknown>): VacationRequest {
        const sellRequest = (data.sellRequest ?? {}) as Record<string, unknown>;

        return {
            id,
            collaboratorId: String(data.collaboratorId ?? ''),
            collaboratorEmail: String(data.collaboratorEmail ?? ''),
            collaboratorName: String(data.collaboratorName ?? ''),
            acquisitionPeriodId: String(data.acquisitionPeriodId ?? ''),
            modality: (data.modality ?? '30') as VacationRequest['modality'],
            segments: (data.segments ?? []) as VacationRequest['segments'],
            totalDays: Number(data.totalDays ?? 0),
            sellRequest: {
                requested: Boolean(sellRequest.requested),
                days: Number(sellRequest.days ?? SELL_DAYS),
                status: (sellRequest.status ?? 'None') as VacationRequest['sellRequest']['status'],
                note: (sellRequest.note ?? null) as string | null,
                decidedBy: (sellRequest.decidedBy ?? null) as string | null,
                decidedAt: (sellRequest.decidedAt ?? null) as Timestamp | null,
            },
            status: (data.status ?? 'PendingManager') as VacationRequest['status'],
            clientEmails: this.readClientEmails(data),
            clientInvites: (data.clientInvites ?? null) as ClientInvite[] | null,
            managerEmail: (data.managerEmail ?? null) as string | null,
            managerApproval: (data.managerApproval ?? {
                status: 'Pending',
            }) as VacationRequest['managerApproval'],
            clientApproval: (data.clientApproval ?? {
                status: 'None',
            }) as VacationRequest['clientApproval'],
            dpApproval: (data.dpApproval ?? {
                status: 'Pending',
            }) as VacationRequest['dpApproval'],
            signature: (data.signature ?? {}) as VacationRequest['signature'],
            conflictsSnapshot: (data.conflictsSnapshot ??
                []) as VacationRequest['conflictsSnapshot'],
            balanceDaysSnapshot: (data.balanceDaysSnapshot ?? null) as number | null,
            concessiveDeadline: (data.concessiveDeadline ?? null) as string | null,
            requestedAt: data.requestedAt as Timestamp,
            requestedBy: String(data.requestedBy ?? ''),
            createdAt: data.createdAt as Timestamp,
            updatedAt: data.updatedAt as Timestamp,
            statusChangedAt: (data.statusChangedAt ?? null) as Timestamp | null,
            statusChangedBy: (data.statusChangedBy ?? null) as string | null,
            receipt: (data.receipt ?? null) as VacationRequest['receipt'],
            receiptSegmentOrders: (data.receiptSegmentOrders ?? null) as number[] | null,
            dpSegmentDecisions: (data.dpSegmentDecisions ?? null) as Record<string, any> | null,
            clientSegmentDecisions: (data.clientSegmentDecisions ?? null) as Record<string, any> | null,
            correctionRevision: (data.correctionRevision ?? null) as number | null,
            lastCorrectionRejection: (data.lastCorrectionRejection ?? null) as VacationRequest['lastCorrectionRejection'],
            rejectionReason: (data.rejectionReason ?? null) as string | null,
            expiredAtStage: (data.expiredAtStage ?? null) as VacationRequestStatus | null,
            deadlineDate: (data.deadlineDate ?? null) as string | null,
        };
    }
}

export const vacationRequestService = new VacationRequestServiceClass();
